import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';

/**
 * "What's New" authoring (managers+). Create / edit / publish-toggle / delete
 * staff announcements, each tagged to a portal. The API enforces managers-only;
 * a non-manager sees a permission error.
 */
interface Announcement {
  id: number;
  title: string;
  body: string;
  target_portal: string;
  is_active: boolean;
  created_at: string;
  published_at: string | null;
  created_by: string | null;
  seen_count: number;
}

const PORTALS = [
  { value: 'ADMIN', label: 'Admin Portal' },
  { value: 'POS', label: 'POS / Store' },
  { value: 'MILL', label: 'Mill' },
];

const emptyForm = { title: '', body: '', target_portal: 'ADMIN', is_active: false };

const AnnouncementsAdmin: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/announcements/');
      setAnnouncements(res.data.announcements || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not load announcements (managers only).');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm(emptyForm); setEditingId(null); };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { setError('Title and body are required.'); return; }
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await api.patch(`/admin/announcements/${editingId}/`, form);
      } else {
        await api.post('/admin/announcements/', form);
      }
      resetForm();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not save the announcement.');
    } finally {
      setSaving(false);
    }
  };

  const edit = (a: Announcement) => {
    setEditingId(a.id);
    setForm({ title: a.title, body: a.body, target_portal: a.target_portal, is_active: a.is_active });
  };

  const toggleActive = async (a: Announcement) => {
    try {
      await api.patch(`/admin/announcements/${a.id}/`, { is_active: !a.is_active });
      await load();
    } catch { setError('Could not change publish state.'); }
  };

  const remove = async (a: Announcement) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/admin/announcements/${a.id}/`);
      await load();
    } catch { setError('Could not delete.'); }
  };

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '1.5rem' }}>
      <h1 style={{ fontSize: '1.5rem', color: '#1a4d1a' }}>What&apos;s New — Announcements</h1>
      {error && <div style={{ color: '#b91c1c', margin: '8px 0' }}>{error}</div>}

      {/* Create / edit form */}
      <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit announcement' : 'New announcement'}</h3>
        <input
          type="text" placeholder="Title" value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          style={{ width: '100%', marginBottom: 8, padding: 8 }}
        />
        <textarea
          placeholder="Body (plain text — line breaks are preserved)" value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          rows={5} style={{ width: '100%', marginBottom: 8, padding: 8 }}
        />
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label>
            Portal:{' '}
            <select value={form.target_portal} onChange={(e) => setForm({ ...form, target_portal: e.target.value })}>
              {PORTALS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </label>
          <label>
            <input type="checkbox" checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Published (active)
          </label>
          <button onClick={save} disabled={saving} style={{ padding: '6px 14px', background: '#1a4d1a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
            {editingId ? 'Save changes' : 'Create'}
          </button>
          {editingId && <button onClick={resetForm} style={{ padding: '6px 12px' }}>Cancel</button>}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <p>Loading…</p>
      ) : announcements.length === 0 ? (
        <p style={{ color: '#6b7280' }}>No announcements yet.</p>
      ) : (
        <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>
              <th>Title</th><th>Portal</th><th>Status</th><th>Seen</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {announcements.map((a) => (
              <tr key={a.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td>{a.title}</td>
                <td>{a.target_portal}</td>
                <td>{a.is_active
                  ? <span style={{ color: '#166534', fontWeight: 700 }}>Published</span>
                  : <span style={{ color: '#9ca3af' }}>Draft</span>}</td>
                <td>{a.seen_count}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button onClick={() => edit(a)}>Edit</button>{' '}
                  <button onClick={() => toggleActive(a)}>{a.is_active ? 'Unpublish' : 'Publish'}</button>{' '}
                  <button onClick={() => remove(a)} style={{ color: '#b91c1c' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AnnouncementsAdmin;
